import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNativeNotWeb } from '@/utils/platform-check';
import { reportOtaProblem } from '@/utils/ota';
import { fetchOtaBundle, findOtaUpdate } from '@/utils/ota-manual-check';

/**
 * The live update a member gets straight after signing in.
 *
 * Channel assignment waits for sign-in (syncOtaChannel in ota.js), but the
 * plugin's own check runs when the process starts, before any JS. So on a fresh
 * install the launch that signs in checks while still unassigned and is offered
 * nothing; the next launch downloads, and the one after applies — three
 * launches before a new member runs the channel's bundle.
 *
 * This closes that gap. Run once, right after a sign-in newly assigns the
 * channel: ask Capgo, and if there is an update, download it behind the
 * "Updating" screen and restart into it. The page the member is on is saved
 * first, as a pending deep link, so the restarted app returns there through the
 * normal post-sign-in navigation (onboarding and force update still apply).
 *
 * A slow network does not hold the member: past `timeoutMs` the screen goes,
 * the download carries on, and the finished bundle is queued with `next()`. The
 * plugin applies a queued bundle when the app goes to the background, so the
 * page is saved at that moment instead.
 *
 * Additive, like the Me page's icon: `autoUpdate` still checks and installs on
 * its own, so a bundle without this file strands nobody. Never throws — the
 * member has just signed in, and nothing here may stop them using the app.
 */

/** How long the "Updating" screen may hold a member before they carry on. */
export const SIGN_IN_UPDATE_TIMEOUT_MS = 15000;

export const SIGN_IN_UPDATE_RESULT = Object.freeze({
  UNSUPPORTED: 'unsupported',
  UP_TO_DATE: 'up-to-date',
  INSTALLING: 'installing',
  DEFERRED: 'deferred',
  FAILED: 'failed',
});

const LABEL = 'OTA sign-in update';

/** Resolves to `null` after `ms`, so it can lose a race to the download. */
function deadline(ms) {
  return new Promise(resolve => setTimeout(() => resolve(null), ms));
}

function reportFailure(step, version, error) {
  console.warn(`[OTA] Sign-in update failed (${step}):`, error?.message || error);
  reportOtaProblem(`${LABEL} failed`, {
    step,
    version,
    reason: error?.message || String(error),
  });
}

/** Saving the page is a courtesy: a failure must not cost the member the update. */
function trySave(saveReturnRoute) {
  try {
    saveReturnRoute();
  } catch (error) {
    console.warn('[OTA] Could not save the page to return to:', error?.message || error);
  }
}

/**
 * Queue a download that outlived the time limit, and save the page when the
 * app next goes to the background — the moment the plugin applies it.
 */
function queueWhenDownloaded(downloading, version, saveReturnRoute) {
  downloading
    .then(async bundle => {
      await CapacitorUpdater.next({ id: bundle.id });
      const handle = await App.addListener('pause', () => {
        trySave(saveReturnRoute);
        handle?.remove?.();
      });
    })
    .catch(error => reportFailure('background download', version, error));
}

/**
 * @param {object} [options]
 * @param {(percent: number) => void} [options.onProgress] first called (0) only
 *   when there is an update to download — the cue to show the screen
 * @param {() => void} [options.saveReturnRoute] records the page to come back to
 * @param {number} [options.timeoutMs]
 * @returns {Promise<string>} one of SIGN_IN_UPDATE_RESULT
 */
export async function updateAfterSignIn({
  onProgress = () => {},
  saveReturnRoute = () => {},
  timeoutMs = SIGN_IN_UPDATE_TIMEOUT_MS,
} = {}) {
  if (!isNativeNotWeb()) return SIGN_IN_UPDATE_RESULT.UNSUPPORTED;

  const found = await findOtaUpdate({ label: LABEL });
  if (found.status === 'failed') return SIGN_IN_UPDATE_RESULT.FAILED;
  if (found.status !== 'available') return SIGN_IN_UPDATE_RESULT.UP_TO_DATE;

  const { latest } = found;
  // Progress only while the member is held. The download outlives the screen
  // when it is slower than the limit, and its later progress must not bring
  // the screen back: on 1.3.9 (256) it did, and stuck at 100%.
  let holding = true;
  const downloading = fetchOtaBundle(latest, percent => {
    if (holding) onProgress(percent);
  });

  let bundle;
  try {
    bundle = await Promise.race([downloading, deadline(timeoutMs)]);
  } catch (error) {
    holding = false;
    reportFailure('install', latest.version, error);
    return SIGN_IN_UPDATE_RESULT.FAILED;
  }

  if (!bundle) {
    holding = false;
    queueWhenDownloaded(downloading, latest.version, saveReturnRoute);
    return SIGN_IN_UPDATE_RESULT.DEFERRED;
  }

  // Before set(): it replaces the running JS, so nothing after it runs.
  trySave(saveReturnRoute);
  try {
    await CapacitorUpdater.set({ id: bundle.id });
    return SIGN_IN_UPDATE_RESULT.INSTALLING;
  } catch (error) {
    reportFailure('install', latest.version, error);
    return SIGN_IN_UPDATE_RESULT.FAILED;
  }
}
